"use client";

import { AnimatePresence, motion } from "motion/react";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { useMediaQuery } from "@/lib/hooks/use-media-query";
import { usePaymentLink } from "@/lib/hooks/use-payment-link";
import { usePrevious } from "@/lib/hooks/use-previous";
import {
  type EmbedConfig,
  EmbedConfigSchema,
} from "@/lib/schemas/checkout/embed-config";
import { useCheckoutStore } from "@/lib/store/checkout";
import PersonalizeLink from "../personalize-link";
import { LinkError } from "../receipt/link-error";
import SupportButton from "../support-button";
import { ScrollArea } from "../ui/scroll-area";
import { AddressForm } from "./address-form";
import { CheckoutSkeleton } from "./checkout-skeleton";
import { CustomerForm } from "./customer-form";
import { DataPane } from "./data-pane";
import { EditProducts } from "./edit-products";
import { PaymentStep } from "./payment-step";
import { Stepper } from "./stepper";

interface PaymentLinkProps {
  hash: string;
  embedded?: boolean;
}

const stepVariants = {
  enter: (direction: number) => ({
    transform: `translateX(${direction > 0 ? 200 : -200}px) scale(0.98)`,
    opacity: 0,
  }),
  center: { transform: "translateX(0px) scale(1)", opacity: 1 },
  exit: (direction: number) => ({
    transform: `translateX(${direction > 0 ? -200 : 200}px) scale(0.98)`,
    opacity: 0,
  }),
};

export const PaymentLink = ({ hash, embedded = false }: PaymentLinkProps) => {
  const router = useRouter();
  const { link, status, error, refetch } = usePaymentLink(hash);
  const currentStep = useCheckoutStore((s) => s.currentStep);
  const steps = useCheckoutStore((s) => s.steps);

  const paidRedirect = !!link?.paidAt && link?.linkType === "oneTime";

  const [renderSkeleton, setRenderSkeleton] = useState(true);
  const [embedConfig, setEmbedConfig] = useState<EmbedConfig | null>(null);

  const prevStep = usePrevious(currentStep);
  const direction =
    steps.indexOf(currentStep) >= steps.indexOf(prevStep || "payment") ? 1 : -1;

  const embedStyles = useMemo(() => {
    if (!embedded || !embedConfig?.styles) return undefined;
    return Object.fromEntries(
      Object.entries(embedConfig.styles).filter(([, v]) => v != null),
    );
  }, [embedded, embedConfig]);

  const isDesktop = useMediaQuery("(min-width: 64rem)");
  const RightPane = isDesktop ? ScrollArea : "div";

  const renderStep = () => {
    if (currentStep === "payment")
      return <PaymentStep msi={link?.msi} embedded={embedded} />;
    if (currentStep === "customer") return <CustomerForm />;
    if (currentStep === "shipping" || currentStep === "billing")
      return <AddressForm key={`address-${currentStep}`} />;

    return null;
  };

  useEffect(() => {
    if (!embedded) return;
    if (window.parent === window) return;

    const onMessage = (e: MessageEvent) => {
      if (e.source !== window.parent) return;
      if (e.data?.type !== "checkout:config") return;
      const parsed = EmbedConfigSchema.safeParse(e.data.config);
      if (parsed.success) setEmbedConfig(parsed.data);
      else
        console.warn("Invalid checkout:config payload:", parsed.error.issues);
    };

    window.addEventListener("message", onMessage);
    window.parent.postMessage({ type: "checkout:ready" }, "*");

    return () => window.removeEventListener("message", onMessage);
  }, [embedded]);

  useEffect(() => {
    if (!paidRedirect) return;
    if (link?.redirectUrl) window.location.assign(link.redirectUrl);
    else router.replace(`/${hash}/receipt`);
  }, [paidRedirect, link?.redirectUrl, hash, router]);

  if (paidRedirect) return <CheckoutSkeleton />;

  if (status === "error" || link?.expired)
    return (
      <LinkError
        error={
          link?.expired || error === "expired" ? "linkExpired" : "linkFetch"
        }
        onRefetch={refetch}
      />
    );

  return (
    <div className="relative">
      {renderSkeleton && (
        <div className="absolute top-0 right-0 left-0">
          <CheckoutSkeleton
            link={link}
            embedded={embedded}
            embedConfig={embedConfig}
          />
        </div>
      )}
      {status !== "loading" && (
        <>
          <motion.div
            className="relative flex min-h-dvh w-dvw flex-col space-y-8 lg:h-dvh lg:flex-row lg:space-y-0"
            initial={{ clipPath: "inset(0 0% 100% 0)" }}
            animate={{ clipPath: "inset(0 0% 0% 0)" }}
            transition={{ duration: 0.27, ease: [0.77, 0, 0.175, 1] }}
            onAnimationComplete={() => setRenderSkeleton(false)}
          >
            <DataPane
              link={link}
              embedded={embedded}
              embedConfig={embedConfig}
            />
            <RightPane className="flex w-full flex-col items-center overflow-x-hidden bg-surface px-4 md:px-0 lg:h-full lg:basis-1/2 lg:items-start">
              <div className="h-full w-full max-w-md space-y-8 md:max-w-lg lg:pt-16 lg:pl-8 2xl:max-w-2xl">
                <Stepper />
                <AnimatePresence
                  initial={false}
                  mode="popLayout"
                  custom={direction}
                >
                  <motion.div
                    key={`crs-${currentStep}`}
                    custom={direction}
                    variants={stepVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.18, ease: "easeOut" }}
                  >
                    {renderStep()}
                  </motion.div>
                </AnimatePresence>
                {embedded && <SupportButton embedded />}
              </div>
            </RightPane>
          </motion.div>
          <EditProducts />
          {link && <PersonalizeLink {...link.linkConfig} {...embedStyles} />}
          {!embedded && <SupportButton />}
        </>
      )}
    </div>
  );
};
